import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import appConstants from '../constants/appConstants';
import { useAppContext } from '../context/AppContext';
import { Form, Spinner, Image, Button, Modal, Badge } from 'react-bootstrap';

const getAuthToken = () => localStorage.getItem('authToken');

const SwipeCard = () => {
  const navigate = useNavigate();
  const { selectedAcademicYear } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [recentStudents, setRecentStudents] = useState([]);
  const [cardNumber, setCardNumber] = useState('');
  const inputRef = useRef(null);
  const selectionInitializedRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Batch selection states
  const [batches, setBatches] = useState([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // Fetch batches when academic year changes
  useEffect(() => {
    inputRef.current?.focus();
    fetchBatches();
  }, [selectedAcademicYear]);

  const fetchBatches = async () => {
    const token = getAuthToken();
    if (!token) return;
    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      selectionInitializedRef.current = false;
      setBatches([]);
      setSelectedBatchIds([]);
      setShowModal(false);
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v3`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );

      const batchData = (response.data.data || []).map((b) => ({
        ...b,
        id: Number(b.id),
      }));

      setBatches(batchData);

      setSelectedBatchIds((prev) => {
        if (prev.length > 0 || selectionInitializedRef.current) {
          return prev;
        }

        selectionInitializedRef.current = true;
        return batchData.length > 0 ? batchData.map((b) => b.id) : [];
      });

      // Open modal if no batch selection has been initialized yet
      if (batchData.length > 0 && !selectionInitializedRef.current) {
        setShowModal(true);
      }
    } catch (err) {
      console.error('Error loading batches:', err);
      setErrorMessage('Failed to load batches.');
    }
  };

  // Auto-clear error messages
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Auto-clear success animation
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Always keep focus on hidden input for RFID scanner
  useEffect(() => {
    const handleKeyPress = () => {
      if (document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  const toggleBatchSelection = (batchId) => {
    selectionInitializedRef.current = true;
    const id = Number(batchId);
    setSelectedBatchIds((prev) =>
      prev.includes(id) ? prev.filter((id2) => id2 !== id) : [...prev, id],
    );
  };

  const handleSelectAllBatches = () => {
    selectionInitializedRef.current = true;
    setSelectedBatchIds(batches.map((batch) => batch.id));
  };

  const handleDeselectAllBatches = () => {
    selectionInitializedRef.current = true;
    setSelectedBatchIds([]);
  };

  const handleConfirmBatches = () => {
    selectionInitializedRef.current = true;
    if (selectedBatchIds.length === 0) {
      setErrorMessage('Please select at least one batch!');
      return;
    }
    setShowModal(false);
    inputRef.current.focus();
  };

  const markAttendanceByRFID = async () => {
    if (!cardNumber) return;

    setLoading(true);
    setErrorMessage(null);
    const token = getAuthToken();
    if (!token) {
      setErrorMessage('Unauthorized! Please log in.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}swipe-attendance-v2`,
        { card_number: cardNumber, batch_ids: selectedBatchIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const student = response.data.student;
      const batchIdsMarked = response.data.batches_marked || [];

      // Map batch IDs to names for display
      const batchNames = batches
        .filter((b) => batchIdsMarked.includes(b.id))
        .map((b) => `${b.class_name} ${b.division}`);

      const studentData = {
        ...student,
        batches: batchNames,
        timestamp: new Date().toLocaleTimeString(),
      };

      setCurrentStudent(studentData);
      setShowSuccess(true);
      setRecentStudents((prev) => [studentData, ...prev.slice(0, 19)]);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || 'Error marking attendance.',
      );
    } finally {
      setCardNumber('');
      setLoading(false);
    }
  };

  const selectedBatchDetails = batches.filter((b) =>
    selectedBatchIds.includes(b.id),
  );

  const isLandscape = window.innerWidth > 900;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        overflow: 'hidden',
      }}
    >
      {/* Responsive Layout Container */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isLandscape ? '35% 1fr' : '1fr',
          gap: '20px',
          height: 'calc(100vh - 40px)',
        }}
      >
        {/* Left Column - Top Bar and Hero Section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {/* Top Header Bar */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              padding: '10px 20px',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              minHeight: '50px',
              maxHeight: '50px',
              flexShrink: 0,
            }}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate('/dashboard')}
              style={{
                borderRadius: '8px',
                padding: '8px',
                width: '38px',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Back to Dashboard"
            >
              <i
                className="fas fa-arrow-left"
                style={{ fontSize: '1.1rem' }}
              ></i>
            </Button>

            <div
              style={{
                flex: 1,
                textAlign: 'center',
                paddingLeft: '10px',
                paddingRight: '10px',
              }}
            >
              <div
                style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: '#333',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={
                  selectedBatchDetails.length > 0
                    ? selectedBatchDetails
                        .map((b) => `${b.class_name}-${b.division}`)
                        .join(', ')
                    : 'No batches selected'
                }
              >
                {selectedBatchDetails.length > 0 ? (
                  <>
                    {selectedBatchDetails.length <= 3 ? (
                      selectedBatchDetails
                        .map((b) => `${b.class_name}-${b.division}`)
                        .join(', ')
                    ) : (
                      <>
                        {selectedBatchDetails
                          .slice(0, 2)
                          .map((b) => `${b.class_name}-${b.division}`)
                          .join(', ')}{' '}
                        <span style={{ color: '#667eea', fontWeight: 'bold' }}>
                          +{selectedBatchDetails.length - 2} more
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <span style={{ color: '#999' }}>No batches selected</span>
                )}
              </div>
            </div>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowModal(true)}
              style={{
                borderRadius: '8px',
                padding: '8px',
                width: '38px',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Change Batches"
            >
              <i className="fas fa-cog" style={{ fontSize: '1.1rem' }}></i>
            </Button>
          </div>

          {/* Batch Selection Modal */}
          <Modal show={showModal} backdrop="static" centered size="lg">
            <Modal.Header
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderBottom: 'none',
                padding: '15px 20px',
              }}
            >
              <Modal.Title style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                <i className="fas fa-users-cog me-2"></i>
                Select Batches
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '20px' }}>
              {batches.length === 0 ? (
                <div className="text-center py-3">
                  <Spinner animation="border" style={{ color: '#667eea' }} />
                  <div
                    className="mt-2"
                    style={{ color: '#666', fontSize: '0.9rem' }}
                  >
                    Loading batches...
                  </div>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      marginBottom: '12px',
                    }}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleSelectAllBatches}
                      style={{ borderRadius: '999px', padding: '4px 12px' }}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDeselectAllBatches}
                      style={{ borderRadius: '999px', padding: '4px 12px' }}
                    >
                      Deselect All
                    </Button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: '6px',
                    }}
                  >
                    {batches.map((batch) => (
                      <button
                        type="button"
                        key={batch.id}
                        onClick={() => toggleBatchSelection(batch.id)}
                        style={{
                          background: selectedBatchIds.includes(batch.id)
                            ? '#edf9f0'
                            : '#fff',
                          color: selectedBatchIds.includes(batch.id)
                            ? '#166534'
                            : '#333',
                          border: selectedBatchIds.includes(batch.id)
                            ? '2px solid #16a34a'
                            : '1px solid #9ca3af',
                          borderRadius: '8px',
                          padding: '8px 10px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: selectedBatchIds.includes(batch.id)
                            ? '0 2px 8px rgba(74, 222, 128, 0.2)'
                            : '0 1px 3px rgba(0,0,0,0.05)',
                          position: 'relative',
                          textAlign: 'center',
                          minHeight: '78px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedBatchIds.includes(batch.id)) {
                            e.currentTarget.style.borderColor = '#667eea';
                            e.currentTarget.style.transform =
                              'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedBatchIds.includes(batch.id)) {
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {selectedBatchIds.includes(batch.id) && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '6px',
                              right: '6px',
                              fontSize: '0.75rem',
                              opacity: 1,
                              color: '#16a34a',
                              background: '#dcfce7',
                              border: '1px solid #86efac',
                              borderRadius: '999px',
                              width: '22px',
                              height: '22px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <i className="fas fa-check"></i>
                          </span>
                        )}
                        <div style={{ fontWeight: '700', fontSize: '0.88rem' }}>
                          {batch.class_name}
                        </div>
                        <div
                          style={{
                            fontSize: '0.7rem',
                            marginTop: '2px',
                            opacity: 0.9,
                          }}
                        >
                          {batch.division}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </Modal.Body>
            <Modal.Footer
              style={{
                borderTop: '1px solid #dee2e6',
                padding: '12px 20px',
                background: '#f8f9fa',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <Button
                variant="light"
                onClick={() => setShowModal(false)}
                style={{
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  border: '2px solid #dee2e6',
                  color: '#666',
                }}
              >
                <i className="fas fa-times me-1"></i>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmBatches}
                style={{
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                }}
              >
                <i className="fas fa-check me-1"></i>
                Confirm ({selectedBatchIds.length})
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Hidden Input for RFID Scanner */}
          <Form onSubmit={(e) => e.preventDefault()}>
            <Form.Control
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && markAttendanceByRFID()}
              ref={inputRef}
              autoFocus
              style={{ position: 'absolute', opacity: 0, left: '-9999px' }}
              tabIndex={-1}
            />
          </Form>

          {/* Error Message */}
          {errorMessage && (
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#ff4444',
                color: 'white',
                padding: '30px 50px',
                borderRadius: '20px',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                boxShadow: '0 10px 40px rgba(255, 68, 68, 0.4)',
                zIndex: 9999,
                animation: 'shake 0.5s',
              }}
            >
              <i className="fas fa-exclamation-triangle me-3"></i>
              {errorMessage}
            </div>
          )}

          {/* Hero Section - Current Student */}
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '15px',
              padding: '50px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '300px',
              flex: 1,
            }}
          >
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <Spinner
                  animation="border"
                  style={{ width: '50px', height: '50px', color: '#667eea' }}
                />
              </div>
            )}

            {!currentStudent ? (
              <div style={{ textAlign: 'center', color: '#999' }}>
                <i
                  className="fas fa-id-card"
                  style={{
                    fontSize: 'clamp(3rem, 8vw, 6rem)',
                    marginBottom: 'clamp(10px, 2vh, 20px)',
                    opacity: 0.3,
                  }}
                ></i>
                <h2
                  style={{
                    fontSize: 'clamp(1.2rem, 3vw, 2.5rem)',
                    fontWeight: '300',
                  }}
                >
                  Waiting for Card Swipe...
                </h2>
                <p
                  style={{
                    fontSize: 'clamp(0.9rem, 2vw, 1.5rem)',
                    marginTop: '5px',
                  }}
                >
                  Please scan your RFID card
                </p>
              </div>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  animation: showSuccess
                    ? 'slideInScale 0.5s ease-out'
                    : 'none',
                }}
              >
                {/* Success Checkmark Animation */}
                {showSuccess && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      animation: 'successPulse 0.6s ease-out',
                    }}
                  >
                    <i
                      className="fas fa-check-circle"
                      style={{
                        fontSize: 'clamp(2rem, 5vw, 4rem)',
                        color: '#28a745',
                      }}
                    ></i>
                  </div>
                )}

                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    marginBottom: 'clamp(10px, 2vh, 20px)',
                  }}
                >
                  <Image
                    src={
                      currentStudent.photo
                        ? `${appConstants.API_ROOT_URL}uploads/students/${currentStudent.photo}`
                        : `${appConstants.API_ROOT_URL}uploads/students/blank.png`
                    }
                    roundedCircle
                    style={{
                      width: 'clamp(100px, 20vw, 200px)',
                      height: 'clamp(100px, 20vw, 200px)',
                      objectFit: 'cover',
                      border: 'clamp(3px, 0.5vw, 6px) solid #667eea',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                    }}
                  />
                  <Badge
                    bg="success"
                    style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '8px',
                      fontSize: 'clamp(0.7rem, 1.5vw, 1.2rem)',
                      padding: 'clamp(4px, 1vw, 8px) clamp(8px, 2vw, 15px)',
                      borderRadius: '15px',
                    }}
                  >
                    <i className="fas fa-check"></i> Present
                  </Badge>
                </div>

                <h1
                  style={{
                    fontSize: 'clamp(1.5rem, 4vw, 3.5rem)',
                    fontWeight: 'bold',
                    color: '#333',
                    marginBottom: 'clamp(6px, 1.5vh, 15px)',
                  }}
                >
                  {currentStudent.name}
                </h1>

                <div
                  style={{
                    display: 'flex',
                    gap: 'clamp(6px, 1vw, 12px)',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {currentStudent.batches.map((batch, idx) => (
                    <Badge
                      key={idx}
                      bg="primary"
                      style={{
                        fontSize: 'clamp(0.85rem, 2vw, 1.5rem)',
                        padding:
                          'clamp(5px, 1vw, 10px) clamp(12px, 2.5vw, 25px)',
                        borderRadius: 'clamp(8px, 1.5vw, 15px)',
                        fontWeight: '600',
                      }}
                    >
                      {batch}
                    </Badge>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 'clamp(8px, 1.5vh, 15px)',
                    fontSize: 'clamp(0.8rem, 1.8vw, 1.3rem)',
                    color: '#666',
                    fontWeight: '500',
                  }}
                >
                  <i className="fas fa-clock me-1"></i>
                  {currentStudent.timestamp}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Recent Students List */}
        {recentStudents.length > 0 && (
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              padding: '15px',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <h6
              style={{
                marginBottom: '12px',
                color: '#333',
                fontWeight: '600',
                fontSize: '0.95rem',
              }}
            >
              <i className="fas fa-history me-2"></i>
              Recent Check-ins
            </h6>
            <div
              style={{
                columnCount: isLandscape ? 'auto' : 1,
                columnWidth: isLandscape ? '280px' : 'auto',
                columnGap: '12px',
                overflowY: 'auto',
                overflowX: 'auto',
                flex: 1,
                paddingRight: '8px',
              }}
            >
              {recentStudents.map((student, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    transition: 'all 0.2s',
                    height: 'fit-content',
                    marginBottom: '10px',
                    breakInside: 'avoid',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow =
                      '0 4px 8px rgba(102, 126, 234, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#e9ecef';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Image
                    src={
                      student.photo
                        ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`
                        : `${appConstants.API_ROOT_URL}uploads/students/blank.png`
                    }
                    roundedCircle
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'cover',
                      border: '2px solid #667eea',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {student.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      {student.batches.join(', ')}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: '0.7rem',
                      color: '#999',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    <i className="fas fa-clock me-1"></i>
                    {student.timestamp}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInScale {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes successPulse {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes shake {
          0%, 100% { transform: translate(-50%, -50%) translateX(0); }
          25% { transform: translate(-50%, -50%) translateX(-10px); }
          75% { transform: translate(-50%, -50%) translateX(10px); }
        }

        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background: #667eea;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #764ba2;
        }
      `}</style>
    </div>
  );
};

export default SwipeCard;
